import React, { ReactNode } from 'react';
import { Box } from '../../legacy/Box';
import { BannerAction } from './BannerAction';
import { BannerDescription } from './BannerDescription';
import { BannerDismiss } from './BannerDismiss';
import { BannerIcon } from './BannerIcon';
import { BannerPane } from './BannerPane';
import { BannerRoot } from './BannerRoot';
import { BannerTitle } from './BannerTitle';

interface ActionType {
  label: string;
  onClick: () => void;
}

export interface BannerProps {
  /**
   * A title for the banner.
   */
  title?: string;
  /**
   * A description for the banner. Can be a basic string or a ReactNode (for more complex content like InlineLinks).
   */
  description: ReactNode;
  /**
   * An action to be displayed in the banner.
   */
  actions?: ActionType[];
  /**
   * The variant of the banner. This will change the background color and icon.
   * @default default
   */
  variant?: 'default' | 'success' | 'warning' | 'danger';
  /**
   * A callback to be invoked when the banner is dismissed.
   */
  onDismiss?: () => void;
}

export const Banner: React.FC<BannerProps> = ({
  title,
  description,
  actions,
  variant = 'default',
  onDismiss,
  ...rest
}) => (
  <BannerRoot data-component="Banner" variant={variant} {...rest}>
    <BannerPane>
      <Box display="flex" alignItems="center" gap={12} padding={12} width={1}>
        <Box marginLeft={4}>
          <BannerIcon />
        </Box>
        <Box display="flex" flexDirection="column" flex={1}>
          {title && <BannerTitle>{title}</BannerTitle>}
          <BannerDescription>{description}</BannerDescription>
        </Box>
        {actions &&
          actions.map(action => (
            <BannerAction key={action.label} onClick={action.onClick}>
              {action.label}
            </BannerAction>
          ))}
        {onDismiss && <BannerDismiss onDismiss={onDismiss} />}
      </Box>
    </BannerPane>
  </BannerRoot>
);
